/**
 * An Area's brief (slice 6d spec §5.4, §11): read, saved and undone by anyone
 * in the crew, and its versions. `['area-brief', id]` is the editor's read;
 * a `memory` hub event with `area_id` refreshes it and the Area page
 * (`app/events.ts`). A save or Undo puts the file it answers into the cache,
 * and a `409 conflict` the brief as it is now.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, errorHint } from "../../app/api";
import type { Author, BriefChange } from "./brief";

const briefKey = (areaId: string) => ["area-brief", areaId] as const;

export type BriefFile = {
  id: string;
  area_id: string;
  name: string;
  content: string;
  version: number;
  length: number;
  limit: number;
  updated_by: Author;
  updated_at: string;
};

/** A doc the brief mentions (spec §5.5): its title now, last change, and whether it's archived. */
export type BriefMention = {
  doc_id: string;
  title: string;
  /** Null for a doc in a private Area the reader isn't in (slice 6g §3.2). */
  updated_at: string | null;
  archived: boolean;
  hidden?: true;
};

export type Brief = { file: BriefFile; recent: BriefChange[]; mentions?: BriefMention[] };

type BriefVersions = {
  versions: BriefChange[];
  first: { version: number; updated_by: Author; updated_at: string } | null;
  next_before: number | null;
};

export function useBrief(areaId: string) {
  return useQuery({
    queryKey: briefKey(areaId),
    queryFn: () => api<Brief>("GET", `/api/areas/${areaId}/brief`),
  });
}

/** All N versions (desktop only): 30 a page, newest first. */
export function useBriefVersions(areaId: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: [...briefKey(areaId), "versions"],
    enabled,
    initialPageParam: null as number | null,
    queryFn: ({ pageParam }) =>
      api<BriefVersions>(
        "GET",
        `/api/areas/${areaId}/brief/versions${pageParam ? `?before=${pageParam}` : ""}`,
      ),
    getNextPageParam: (page) => page.next_before,
  });
}

const isFile = (f: Partial<BriefFile> | undefined): f is BriefFile =>
  typeof f?.content === "string" && Number.isInteger(f.version);

/** A `409 conflict`'s brief as it is now, else null. */
export function conflictFile(error: unknown): BriefFile | null {
  const conflict = error instanceof ApiError && error.code === "conflict";
  const f = conflict ? (error.body.file as BriefFile | undefined) : undefined;
  return isFile(f) ? f : null;
}

/** A refused save: the newer brief it ran into, or else what to say. */
export function briefRefusal(error: unknown): {
  conflict: BriefFile | null;
  problem: string | null;
} {
  const conflict = conflictFile(error);
  return { conflict, problem: error && !conflict ? errorHint(error) : null };
}

/** The cached brief with `file` in it, unless the cache holds a newer version. */
export function withFile(old: Brief | undefined, file: BriefFile): Brief {
  if (old && old.file.version > file.version) return old;
  return { recent: old?.recent ?? [], file };
}

/** The file into the cache (an older one never replaces a newer), and Recent changes refetched. */
function useStore(areaId: string) {
  const client = useQueryClient();
  return (file: BriefFile) => {
    client.setQueryData<Brief>(briefKey(areaId), (old) => withFile(old, file));
    void client.invalidateQueries({ queryKey: briefKey(areaId) });
    void client.invalidateQueries({ queryKey: ["area"] });
  };
}

function useBriefWrite<T>(areaId: string, write: (input: T) => Promise<{ file: BriefFile }>) {
  const store = useStore(areaId);
  return useMutation({
    mutationFn: async (input: T) => (await write(input)).file,
    onSuccess: store,
    onError: (error) => {
      const now = conflictFile(error);
      if (now) store(now);
    },
  });
}

/** `PUT` the whole brief from the version the page has; a stale one merges, or is `409`. */
export function useSaveBrief(areaId: string) {
  return useBriefWrite(areaId, (edit: { content: string; version: number }) =>
    api<{ file: BriefFile }>("PUT", `/api/areas/${areaId}/brief`, edit),
  );
}

/** Undo any recent version's change, as a new version. */
export function useUndoBrief(areaId: string) {
  return useBriefWrite(areaId, (version: number) =>
    api<{ file: BriefFile }>("POST", `/api/areas/${areaId}/brief/undo`, { version }),
  );
}
